import { DatabaseData, DatabaseInfo, TableInfo, ColumnInfo, RowData } from '../types/database'

// sql.jsの型定義
interface SqlJs {
  Database: new (data: Uint8Array) => SqlJsDatabase
}

interface SqlJsDatabase {
  exec(sql: string): SqlJsResult[]
  close(): void
  export(): Uint8Array
}

interface SqlJsResult {
  columns: string[]
  values: any[][]
}

export class SQLiteService {
  private static instance: SQLiteService
  private sqlJs: SqlJs | null = null

  private constructor() {}

  static getInstance(): SQLiteService {
    if (!SQLiteService.instance) {
      SQLiteService.instance = new SQLiteService()
    }
    return SQLiteService.instance
  }

  async initialize(): Promise<void> {
    if (!this.sqlJs) {
      try {
        // 動的インポートでsql.jsを読み込み
        const initSqlJs = (await import('sql.js')).default
        this.sqlJs = await initSqlJs({
          locateFile: (file: string) => `https://sql.js.org/dist/${file}`
        })
      } catch (error) {
        throw new Error(`SQLiteライブラリの初期化に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
      }
    }
  }

  async parseDatabase(buffer: ArrayBuffer): Promise<DatabaseData> {
    await this.initialize()
    
    if (!this.sqlJs) {
      throw new Error('SQLiteライブラリが初期化されていません')
    }

    const db = new this.sqlJs.Database(new Uint8Array(buffer))
    
    try {
      const tables = await this.getTables(db)
      
      const info: DatabaseInfo = {
        type: 'sqlite',
        name: 'SQLite Database',
        size: buffer.byteLength,
        tables: tables.map(table => ({
          name: table.name,
          columns: table.columns,
          rowCount: table.rowCount
        }))
      }

      const tableData: DatabaseData['tables'] = {}
      
      for (const table of tables) {
        const rows = await this.getTableData(db, table.name)
        tableData[table.name] = {
          columns: table.columns,
          rows: rows
        }
      }

      return { info, tables: tableData }
    } finally {
      db.close()
    }
  }

  private async getTables(db: SqlJsDatabase): Promise<(TableInfo & { columns: ColumnInfo[] })[]> {
    const result = db.exec(`
      SELECT name FROM sqlite_master 
      WHERE type='table' AND name NOT LIKE 'sqlite_%'
      ORDER BY name
    `)
    
    const tables: (TableInfo & { columns: ColumnInfo[] })[] = []
    
    for (const row of result[0]?.values || []) {
      const tableName = row[0] as string
      const columns = await this.getTableColumns(db, tableName)
      const rowCount = await this.getTableRowCount(db, tableName)
      
      tables.push({
        name: tableName,
        columns,
        rowCount
      })
    }
    
    return tables
  }

  private async getTableColumns(db: SqlJsDatabase, tableName: string): Promise<ColumnInfo[]> {
    const result = db.exec(`PRAGMA table_info(${tableName})`)
    const columns: ColumnInfo[] = []
    
    for (const row of result[0]?.values || []) {
      columns.push({
        name: row[1] as string,
        type: row[2] as string,
        nullable: !row[3],
        primaryKey: Boolean(row[5])
      })
    }
    
    return columns
  }

  private async getTableRowCount(db: SqlJsDatabase, tableName: string): Promise<number> {
    const result = db.exec(`SELECT COUNT(*) FROM ${tableName}`)
    return result[0]?.values?.[0]?.[0] as number || 0
  }

  private async getTableData(db: SqlJsDatabase, tableName: string): Promise<RowData[]> {
    const result = db.exec(`SELECT * FROM ${tableName}`)
    const rows: RowData[] = []
    
    if (result[0]?.values) {
      const columns = result[0].columns
      const values = result[0].values
      
      for (const row of values) {
        const rowData: RowData = {}
        columns.forEach((col: string, index: number) => {
          rowData[col] = row[index]
        })
        rows.push(rowData)
      }
    }
    
    return rows
  }

  async exportDatabase(data: DatabaseData, originalFileName: string): Promise<Blob> {
    await this.initialize()
    
    if (!this.sqlJs) {
      throw new Error('SQLiteライブラリが初期化されていません')
    }

    try {
      // 新しいデータベースを作成
      const db = new this.sqlJs.Database()
      
      // 各テーブルを作成
      for (const [tableName, table] of Object.entries(data.tables)) {
        // CREATE TABLE文を生成
        const columnDefs = table.columns.map(col => {
          let def = `${col.name} ${col.type}`
          if (!col.nullable) def += ' NOT NULL'
          if (col.primaryKey) def += ' PRIMARY KEY'
          return def
        }).join(', ')
        
        const createTableSQL = `CREATE TABLE ${tableName} (${columnDefs})`
        db.exec(createTableSQL)
        
        // データを挿入
        for (const row of table.rows) {
          const columns = table.columns.map(col => col.name)
          const values = columns.map(col => {
            const value = row[col]
            if (value === null || value === undefined) return 'NULL'
            if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`
            return value
          })
          
          const insertSQL = `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES (${values.join(', ')})`
          db.exec(insertSQL)
        }
      }
      
      // データベースをバイト配列に変換
      const bytes = db.export()
      db.close()
      
      return new Blob([bytes], { type: 'application/x-sqlite3' })
    } catch (error) {
      throw new Error(`SQLiteファイルのエクスポートに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
    }
  }
} 